import React, { useContext, useEffect, useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  Button,
  TextField,
  MenuItem,
  Grid,
  Box,
  IconButton,
  Divider,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import DeleteIcon from '@material-ui/icons/Delete';
import DragIndicatorIcon from '@material-ui/icons/DragIndicator';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import ClearIcon from '@material-ui/icons/Clear';
import SaveIcon from '@material-ui/icons/Save';
import FolderOpenIcon from '@material-ui/icons/FolderOpen';
import AddIcon from '@material-ui/icons/Add';
import LinkIcon from '@material-ui/icons/Link';
import Alert from '@material-ui/lab/Alert';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { Context as LinkBuilderContext } from '../../context/LinkBuilderContext';

const useStyles = makeStyles((theme) => ({
  container: {
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(4),
  },
  paper: {
    padding: theme.spacing(3),
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(3),
  },
  section: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  cartItem: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    border: '1px solid #e0e0e0',
    borderRadius: theme.spacing(1),
    backgroundColor: '#fafafa',
  },
  draggingItem: {
    backgroundColor: '#e3f2fd',
  },
  productImage: {
    maxWidth: 80,
    maxHeight: 80,
    objectFit: 'contain',
    borderRadius: theme.spacing(1),
  },
  dragHandle: {
    cursor: 'grab',
    '&:active': {
      cursor: 'grabbing',
    },
  },
  urlDisplay: {
    backgroundColor: '#f5f5f5',
    padding: theme.spacing(2),
    borderRadius: theme.spacing(1),
    wordBreak: 'break-all',
    fontFamily: 'monospace',
    fontSize: '0.9rem',
  },
  actionButtons: {
    display: 'flex',
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
  previewSection: {
    backgroundColor: '#fff3e0',
    padding: theme.spacing(2),
    borderRadius: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
  chipContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  templateItem: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
}));

const BILLING_MODELS = [
  { id: 2, label: 'One-time purchase' },
  { id: 3, label: '30-day subscription' },
  { id: 4, label: '45-day subscription' },
  { id: 5, label: '60-day subscription' },
  { id: 6, label: '90-day subscription' },
  { id: 7, label: '180-day subscription' },
];

const WEBSITE_BASE_URL = 'https://manna.com/checkout?';

export default function LinkBuilderPage({ onRender }) {
  const classes = useStyles();
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [loadTemplateDialogOpen, setLoadTemplateDialogOpen] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [customParams, setCustomParams] = useState([{ key: '', value: '' }]);
  const [generatedSecureLink, setGeneratedSecureLink] = useState('');

  const {
    state: { availableProducts, cartItems, templates, isLoading },
    getProducts,
    addCartItem,
    updateCartItem,
    removeCartItem,
    reorderCartItems,
    clearCart,
    saveTemplate,
    getTemplates,
    deleteTemplate,
    loadTemplate,
    createSharedLink,
  } = useContext(LinkBuilderContext);

  useEffect(() => {
    onRender('Link Builder');
    getProducts();
    getTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAddProduct = (event, value) => {
    if (value) {
      const newCartItem = {
        id: `${value._id}-${Date.now()}`,
        product: value,
        productId: value.id || '',
        billingModel: 2,
        quantity: 1,
        priceOverride: '',
        offerId: '',
      };
      addCartItem(newCartItem);
    }
  };

  const handleUpdateCartItem = (id, field, value) => {
    const item = cartItems.find((i) => i.id === id);
    if (item) {
      updateCartItem({ ...item, [field]: value });
    }
  };

  const handleRemoveCartItem = (id) => {
    removeCartItem(id);
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(cartItems);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    reorderCartItems(items);
  };

  const handleAddParam = () => {
    setCustomParams([...customParams, { key: '', value: '' }]);
  };

  const handleRemoveParam = (index) => {
    const newParams = customParams.filter((_, i) => i !== index);
    setCustomParams(newParams);
  };

  const handleParamChange = (index, field, value) => {
    const newParams = [...customParams];
    newParams[index][field] = value;
    setCustomParams(newParams);
  };

  const generateURL = () => {
    if (cartItems.length === 0) return '';

    const itemsParam = cartItems
      .map((item) => {
        const { productId, billingModel, quantity, priceOverride, offerId } = item;
        if (!productId) return null;

        if (offerId && offerId.trim() !== '') {
           // Format: productId:billingModel:quantity:priceOverride:offerId
           // If priceOverride is empty, it will be ::offerId
           const price = (priceOverride !== '' && priceOverride !== null && priceOverride !== undefined) ? priceOverride : '';
           return `${productId}:${billingModel}:${quantity}:${price}:${offerId.trim()}`;
        }

        if (priceOverride !== '' && priceOverride !== null && priceOverride !== undefined) {
          return `${productId}:${billingModel}:${quantity}:${priceOverride}`;
        }
        return `${productId}:${billingModel}:${quantity}`;
      })
      .filter(Boolean)
      .join(',');

    let url = itemsParam ? `${WEBSITE_BASE_URL}items=${itemsParam}` : '';

    if (url) {
      const customParamsString = customParams
        .filter((p) => p.key.trim() !== '')
        .map((p) => `&${encodeURIComponent(p.key.trim())}=${encodeURIComponent(p.value.trim())}`)
        .join('');
      url += customParamsString;
    }

    return url;
  };

  const handleCopyURL = () => {
    const url = generateURL();
    if (url) {
      navigator.clipboard.writeText(url);
      showSnackbar('URL copied to clipboard!', 'success');
    } else {
      showSnackbar('No items in cart to generate URL', 'warning');
    }
  };

  const handleTestURL = () => {
    const url = generateURL();
    if (url) {
      window.open(url, '_blank');
    } else {
      showSnackbar('No items in cart to generate URL', 'warning');
    }
  };

  const handleGenerateShortLink = async () => {
    if (cartItems.length === 0) {
      showSnackbar('No items in cart to generate link', 'warning');
      return;
    }

    try {
      const url = generateURL();
      // Extract query string (everything after ?)
      const originalParams = url.split('?')[1];

      const result = await createSharedLink({
        originalParams,
      });
      const shortUrl = `${WEBSITE_BASE_URL}linkid=${result.shortId}`;
      setGeneratedSecureLink(shortUrl);
      showSnackbar('Secure link generated successfully!', 'success');
    } catch (error) {
      showSnackbar('Failed to generate secure link', 'error');
    }
  };

  const handleCopySecureLink = () => {
    navigator.clipboard.writeText(generatedSecureLink);
    showSnackbar('Secure link copied to clipboard!', 'success');
  };

  const handleOpenSecureLink = () => {
    window.open(generatedSecureLink, '_blank');
  };

  const handleClearAll = () => {
    clearCart();
    setCustomParams([{ key: '', value: '' }]);
    setGeneratedSecureLink('');
    showSnackbar('Cart cleared', 'info');
  };

  const handleSaveTemplate = async () => {
    if (!templateName.trim()) {
      showSnackbar('Please enter a template name', 'warning');
      return;
    }

    if (cartItems.length === 0) {
      showSnackbar('Cannot save empty template', 'warning');
      return;
    }

    try {
      await saveTemplate({
        name: templateName,
        cartItems: cartItems,
        customParams: customParams,
      });
      showSnackbar('Template saved successfully!', 'success');
      setTemplateDialogOpen(false);
      setTemplateName('');
    } catch (error) {
      showSnackbar('Failed to save template', 'error');
    }
  };

  const handleLoadTemplate = (template) => {
    loadTemplate(template.cartItems);
    if (template.customParams) {
      setCustomParams(template.customParams);
    } else {
      setCustomParams([{ key: '', value: '' }]);
    }
    setLoadTemplateDialogOpen(false);
    showSnackbar('Template loaded successfully!', 'success');
  };

  const handleDeleteTemplate = async (id, event) => {
    event.stopPropagation();
    try {
      await deleteTemplate(id);
      showSnackbar('Template deleted successfully!', 'success');
    } catch (error) {
      showSnackbar('Failed to delete template', 'error');
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const getProductPrice = (product) => {
    return parseFloat(product.sale_price) || parseFloat(product.price) || 0;
  };

  const getEffectivePrice = (item) => {
    if (item.priceOverride !== '' && item.priceOverride !== null && item.priceOverride !== undefined) {
      return parseFloat(item.priceOverride) || 0;
    }

    const basePrice = getProductPrice(item.product);
    // Apply 20% subscription discount for subscription billing models (3-7)
    const isSubscription = item.billingModel >= 3 && item.billingModel <= 7;

    return isSubscription ? basePrice * 0.8 : basePrice;
  };

  const calculateTotal = () => {
    return cartItems.reduce((total, item) => {
      const price = getEffectivePrice(item);
      return total + price * item.quantity;
    }, 0);
  };

  const generatedURL = generateURL();

  return (
    <Container maxWidth='lg' className={classes.container}>
      <Paper className={classes.paper}>
        <div className={classes.header}>
          <Typography variant='h4' component='h1'>
            Link Builder
          </Typography>
          <Box>
            <Button
              variant='outlined'
              startIcon={<FolderOpenIcon />}
              onClick={() => setLoadTemplateDialogOpen(true)}
              style={{ marginRight: 8 }}
            >
              Load Template
            </Button>
            <Button
              variant='outlined'
              startIcon={<SaveIcon />}
              onClick={() => setTemplateDialogOpen(true)}
              disabled={cartItems.length === 0}
            >
              Save Template
            </Button>
          </Box>
        </div>

        <Divider />

        {/* Product Selection */}
        <div className={classes.section}>
          <Typography variant='h6' gutterBottom>
            1. Select Products
          </Typography>
          <Autocomplete
            value={null}
            onChange={handleAddProduct}
            options={availableProducts}
            getOptionLabel={(option) => option.name || 'Unnamed Product'}
            filterOptions={(options, { inputValue }) => {
              const searchValue = inputValue.toLowerCase().trim();
              if (!searchValue) return options;

              return options.filter((option) => {
                const name = (option.name || '').toLowerCase();
                const id = String(option.id || '');
                const sku = (option.sku || '').toLowerCase();

                return name.includes(searchValue) || id.includes(searchValue) || sku.includes(searchValue);
              });
            }}
            renderOption={(option) => (
              <Box display='flex' alignItems='center'>
                {option.images?.[0]?.path && (
                  <img
                    src={option.images[0].path}
                    alt={option.name}
                    style={{ width: 40, height: 40, marginRight: 10, objectFit: 'contain' }}
                  />
                )}
                <Box>
                  <Typography variant='body1'>{option.name || 'Unnamed Product'}</Typography>
                  <Typography variant='caption' color='textSecondary'>
                    ID: {option.id} | Price: ${getProductPrice(option).toFixed(2)}
                  </Typography>
                </Box>
              </Box>
            )}
            renderInput={(params) => (
              <TextField {...params} label='Search and select products' placeholder='Type to search...' />
            )}
            loading={isLoading}
          />
        </div>

        <Divider />

        {/* Cart Builder */}
        <div className={classes.section}>
          <Box display='flex' justifyContent='space-between' alignItems='center'>
            <Typography variant='h6'>2. Configure Cart Items</Typography>
            {cartItems.length > 0 && (
              <Button startIcon={<ClearIcon />} onClick={handleClearAll} color='secondary'>
                Clear All
              </Button>
            )}
          </Box>

          {cartItems.length === 0 ? (
            <Box mt={2}>
              <Alert severity='info'>No products added yet. Select products from the dropdown above.</Alert>
            </Box>
          ) : (
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId='cart-items'>
                {(provided) => (
                  <div {...provided.droppableProps} ref={provided.innerRef}>
                    {cartItems.map((item, index) => (
                      <Draggable key={item.id} draggableId={item.id} index={index}>
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            className={`${classes.cartItem} ${
                              snapshot.isDragging ? classes.draggingItem : ''
                            }`}
                          >
                            <Grid container spacing={2} alignItems='center'>
                              <Grid item>
                                <div {...provided.dragHandleProps} className={classes.dragHandle}>
                                  <DragIndicatorIcon />
                                </div>
                              </Grid>
                              <Grid item>
                                {item.product.images?.[0]?.path && (
                                  <img
                                    src={item.product.images[0].path}
                                    alt={item.product.name}
                                    className={classes.productImage}
                                  />
                                )}
                              </Grid>
                              <Grid item xs>
                                <Grid container spacing={2}>
                                  <Grid item xs={12}>
                                    <Typography variant='subtitle1' style={{ fontWeight: 'bold' }}>
                                      {item.product.name || 'Unnamed Product'}
                                    </Typography>
                                    <Typography variant='caption' color='textSecondary'>
                                      Product ID: {item.productId} | DB Price: $
                                      {getProductPrice(item.product).toFixed(2)}
                                      {item.billingModel >= 3 && item.billingModel <= 7 && (
                                        <span style={{ color: '#4caf50', fontWeight: 'bold' }}>
                                          {' '}
                                          {item.priceOverride ? (
                                            <>
                                              → ${parseFloat(item.priceOverride).toFixed(2)} (Custom Price)
                                            </>
                                          ) : (
                                            <>
                                              → ${(getProductPrice(item.product) * 0.8).toFixed(2)} (20% subscription discount)
                                            </>
                                          )}
                                        </span>
                                      )}
                                      {!(item.billingModel >= 3 && item.billingModel <= 7) && item.priceOverride && (
                                         <span style={{ color: '#4caf50', fontWeight: 'bold' }}>
                                          {' '}
                                          → ${parseFloat(item.priceOverride).toFixed(2)} (Custom Price)
                                        </span>
                                      )}
                                    </Typography>
                                  </Grid>
                                  <Grid item xs={12} sm={3}>
                                    <TextField
                                      fullWidth
                                      select
                                      label='Billing Model'
                                      value={item.billingModel}
                                      onChange={(e) =>
                                        handleUpdateCartItem(
                                          item.id,
                                          'billingModel',
                                          parseInt(e.target.value)
                                        )
                                      }
                                    >
                                      {BILLING_MODELS.map((model) => (
                                        <MenuItem key={model.id} value={model.id}>
                                          {model.label}
                                        </MenuItem>
                                      ))}
                                    </TextField>
                                  </Grid>
                                  <Grid item xs={12} sm={2}>
                                    <TextField
                                      fullWidth
                                      type='number'
                                      label='Quantity'
                                      value={item.quantity}
                                      onChange={(e) =>
                                        handleUpdateCartItem(
                                          item.id,
                                          'quantity',
                                          parseInt(e.target.value) || 1
                                        )
                                      }
                                      inputProps={{ min: 1 }}
                                    />
                                  </Grid>
                                  <Grid item xs={12} sm={3}>
                                    <TextField
                                      fullWidth
                                      type='number'
                                      label='Price Override'
                                      value={item.priceOverride}
                                      onChange={(e) =>
                                        handleUpdateCartItem(item.id, 'priceOverride', e.target.value)
                                      }
                                      placeholder='DB Price'
                                      helperText='Custom price'
                                      inputProps={{ step: '0.01', min: 0 }}
                                    />
                                  </Grid>
                                  <Grid item xs={12} sm={3}>
                                    <TextField
                                      fullWidth
                                      label='Offer ID (Optional)'
                                      value={item.offerId || ''}
                                      onChange={(e) =>
                                        handleUpdateCartItem(item.id, 'offerId', e.target.value)
                                      }
                                      placeholder='e.g., OFFER123'
                                    />
                                  </Grid>
                                  <Grid item xs={12} sm={1}>
                                    <IconButton
                                      color='secondary'
                                      onClick={() => handleRemoveCartItem(item.id)}
                                      style={{ marginTop: 8 }}
                                    >
                                      <DeleteIcon />
                                    </IconButton>
                                  </Grid>
                                </Grid>
                              </Grid>
                            </Grid>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          )}

          {/* Cart Preview */}
          {cartItems.length > 0 && (
            <Box className={classes.previewSection}>
              <Typography variant='subtitle1' style={{ fontWeight: 'bold', marginBottom: 8 }}>
                Cart Preview:
              </Typography>
              {cartItems.map((item, index) => (
                <Typography key={item.id} variant='body2'>
                  {index + 1}. {item.product.name} -{' '}
                  {BILLING_MODELS.find((m) => m.id === item.billingModel)?.label} × {item.quantity} @ $
                  {getEffectivePrice(item).toFixed(2)}
                  {item.priceOverride !== '' &&
                    item.priceOverride !== null &&
                    item.priceOverride !== undefined && (
                      <Chip
                        label='Custom Price'
                        size='small'
                        color='primary'
                        style={{ marginLeft: 8, height: 20 }}
                      />
                    )}
                  {item.offerId && (
                    <Chip
                      label={`Offer: ${item.offerId}`}
                      size='small'
                      color='secondary'
                      style={{ marginLeft: 8, height: 20 }}
                    />
                  )}
                </Typography>
              ))}
              <Divider style={{ margin: '8px 0' }} />
              <Typography variant='subtitle1' style={{ fontWeight: 'bold' }}>
                Total: ${calculateTotal().toFixed(2)}
              </Typography>
            </Box>
          )}
        </div>

        <Divider />

        {/* Custom Parameters */}
        <div className={classes.section}>
          <Typography variant='h6' gutterBottom>
            3. Custom URL Parameters
          </Typography>
          <Typography variant='body2' color='textSecondary' paragraph>
            Add custom key-value pairs to the URL (e.g., cid=1, shippingid=23).
          </Typography>
          
          {customParams.map((param, index) => (
            <Box key={index} display="flex" alignItems="center" mb={2}>
              <Grid container spacing={2}>
                <Grid item xs={5}>
                  <TextField
                    fullWidth
                    label="Key"
                    value={param.key}
                    onChange={(e) => handleParamChange(index, 'key', e.target.value)}
                    placeholder="e.g., cid"
                    variant="outlined"
                    size="small"
                  />
                </Grid>
                <Grid item xs={5}>
                  <TextField
                    fullWidth
                    label="Value"
                    value={param.value}
                    onChange={(e) => handleParamChange(index, 'value', e.target.value)}
                    placeholder="e.g., 1"
                    variant="outlined"
                    size="small"
                  />
                </Grid>
                <Grid item xs={2}>
                  <IconButton 
                    onClick={() => handleRemoveParam(index)} 
                    disabled={customParams.length === 1 && param.key === '' && param.value === ''}
                    color="secondary"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Grid>
              </Grid>
            </Box>
          ))}
          
          <Button
            startIcon={<AddIcon />}
            onClick={handleAddParam}
            color="primary"
            variant="outlined"
            size="small"
          >
            Add Parameter
          </Button>
        </div>

        <Divider />

        {/* URL Output */}
        <div className={classes.section}>
          <Typography variant='h6' gutterBottom>
            4. Generated Checkout URL
          </Typography>

          {generatedURL ? (
            <>
              <Box className={classes.urlDisplay}>
                <Typography variant='body2'>{generatedURL}</Typography>
              </Box>
              <div className={classes.actionButtons}>
                <Button
                  variant='contained'
                  color='primary'
                  startIcon={<FileCopyIcon />}
                  onClick={handleCopyURL}
                >
                  Copy 
                </Button>
                <Button
                  variant='outlined'
                  color='primary'
                  startIcon={<OpenInNewIcon />}
                  onClick={handleTestURL}
                >
                  Open
                </Button>
                <Button
                  variant='contained'
                  color='secondary'
                  startIcon={<LinkIcon />}
                  onClick={handleGenerateShortLink}
                >
                  Generate Secure Link
                </Button>
              </div>
              
              {/* Display generated secure link */}
              {generatedSecureLink && (
                <Box mt={3}>
                  <Typography variant='subtitle2' gutterBottom style={{ fontWeight: 'bold' }}>
                    Secure Link:
                  </Typography>
                  <Box className={classes.urlDisplay} mb={2}>
                    <Typography variant='body2'>{generatedSecureLink}</Typography>
                  </Box>
                  <div className={classes.actionButtons}>
                    <Button
                      variant='contained'
                      color='primary'
                      startIcon={<FileCopyIcon />}
                      onClick={handleCopySecureLink}
                    >
                      Copy
                    </Button>
                    <Button
                      variant='outlined'
                      color='primary'
                      startIcon={<OpenInNewIcon />}
                      onClick={handleOpenSecureLink}
                    >
                      Open
                    </Button>
                  </div>
                </Box>
              )}
            </>
          ) : (
            <Alert severity='info'>Add products to the cart to generate a checkout URL.</Alert>
          )}
        </div>
      </Paper>

      {/* Save Template Dialog */}
      <Dialog open={templateDialogOpen} onClose={() => setTemplateDialogOpen(false)}>
        <DialogTitle>Save Template</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label='Template Name'
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
            placeholder='e.g., Black Friday Bundle'
            style={{ marginTop: 8 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTemplateDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveTemplate} color='primary' variant='contained'>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Load Template Dialog */}
      <Dialog
        open={loadTemplateDialogOpen}
        onClose={() => setLoadTemplateDialogOpen(false)}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>Load Template</DialogTitle>
        <DialogContent>
          {templates.length === 0 ? (
            <Alert severity='info'>No saved templates yet.</Alert>
          ) : (
            <List>
              {templates.map((template) => (
                <ListItem
                  key={template._id}
                  button
                  className={classes.templateItem}
                  onClick={() => handleLoadTemplate(template)}
                >
                  <ListItemText
                    primary={template.name}
                    secondary={`${template.cartItems?.length || 0} items`}
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge='end'
                      onClick={(e) => handleDeleteTemplate(template._id, e)}
                      color='secondary'
                    >
                      <DeleteIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLoadTemplateDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
}
